// The API base URL should ideally be loaded from environment variables,
// but for this deployment, we'll hardcode the production URL as a fallback.
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://pulsesure-backend.koplee.workers.dev';

// The dashboard secret should be managed via environment variables and not be hardcoded.
// You can create a .env.production.local file in the dashboard package with:
// VITE_DASHBOARD_SECRET="your_secret_value"
const DASHBOARD_SECRET = import.meta.env.VITE_DASHBOARD_SECRET || '';

import type { 
  ApiResponse, 
  StrategyDashboardData, 
  ProductHealthMetrics,
  AiCorePerformanceMetrics,
  EcosystemMetrics,
  TimeSeriesDataPoint,
  DashboardAlert
} from 'shared';

/**
 * A wrapper around the native fetch API to streamline API calls.
 * @param endpoint The API endpoint to call (e.g., '/dashboard/api/user-stats').
 * @param options Fetch options.
 * @returns The JSON response from the API.
 */
export async function apiFetch<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    'X-Dashboard-Secret': DASHBOARD_SECRET,
    ...options.headers,
  };

  const config: RequestInit = {
    ...options,
    headers,
  };

  const response = await window.fetch(`${API_BASE_URL}${endpoint}`, config);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'An unknown error occurred' }));
    return Promise.reject(errorData);
  }

  return response.json() as Promise<T>;
}

// === 战略Dashboard API方法 ===

/**
 * 获取完整的战略Dashboard数据
 */
export async function fetchStrategyDashboard(params?: {
  startDate?: string;
  endDate?: string;
  timeGranularity?: 'hour' | 'day' | 'week' | 'month';
}): Promise<StrategyDashboardData> {
  const searchParams = new URLSearchParams();
  if (params?.startDate) searchParams.set('startDate', params.startDate);
  if (params?.endDate) searchParams.set('endDate', params.endDate);
  if (params?.timeGranularity) searchParams.set('timeGranularity', params.timeGranularity);
  
  const endpoint = `/dashboard/api/strategy${searchParams.toString() ? '?' + searchParams.toString() : ''}`;
  const response = await apiFetch<ApiResponse<StrategyDashboardData>>(endpoint);
  
  if (!response.success || !response.data) {
    throw new Error(response.message || '获取战略Dashboard数据失败');
  }
  
  return response.data;
}

/**
 * 获取产品健康度指标
 */
export async function fetchProductHealth(params?: {
  startDate?: string;
  endDate?: string;
}): Promise<ProductHealthMetrics> {
  const searchParams = new URLSearchParams();
  if (params?.startDate) searchParams.set('startDate', params.startDate);
  if (params?.endDate) searchParams.set('endDate', params.endDate);
  
  const endpoint = `/dashboard/api/product-health${searchParams.toString() ? '?' + searchParams.toString() : ''}`;
  const response = await apiFetch<ApiResponse<ProductHealthMetrics>>(endpoint);
  
  if (!response.success || !response.data) {
    throw new Error(response.message || '获取产品健康度数据失败');
  }
  
  return response.data;
}

/**
 * 获取AI核心性能指标
 */
export async function fetchAiCorePerformance(params?: {
  startDate?: string;
  endDate?: string;
}): Promise<AiCorePerformanceMetrics> {
  const searchParams = new URLSearchParams();
  if (params?.startDate) searchParams.set('startDate', params.startDate);
  if (params?.endDate) searchParams.set('endDate', params.endDate);
  
  const endpoint = `/dashboard/api/ai-core-performance${searchParams.toString() ? '?' + searchParams.toString() : ''}`;
  const response = await apiFetch<ApiResponse<AiCorePerformanceMetrics>>(endpoint);
  
  if (!response.success || !response.data) {
    throw new Error(response.message || '获取AI性能数据失败');
  }
  
  return response.data;
}

/**
 * 获取生态系统指标
 */
export async function fetchEcosystemMetrics(params?: {
  startDate?: string;
  endDate?: string;
}): Promise<EcosystemMetrics> {
  const searchParams = new URLSearchParams();
  if (params?.startDate) searchParams.set('startDate', params.startDate);
  if (params?.endDate) searchParams.set('endDate', params.endDate);
  
  const endpoint = `/dashboard/api/ecosystem${searchParams.toString() ? '?' + searchParams.toString() : ''}`;
  const response = await apiFetch<ApiResponse<EcosystemMetrics>>(endpoint);
  
  if (!response.success || !response.data) {
    throw new Error(response.message || '获取生态系统数据失败');
  }
  
  return response.data;
}

/**
 * 获取时序数据
 */
export async function fetchTimeSeriesData(params: {
  metricName: string;
  aggregationPeriod?: 'hour' | 'day' | 'week' | 'month';
  startDate?: string;
  endDate?: string;
}): Promise<TimeSeriesDataPoint[]> {
  const searchParams = new URLSearchParams();
  searchParams.set('metricName', params.metricName);
  if (params.aggregationPeriod) searchParams.set('aggregationPeriod', params.aggregationPeriod);
  if (params.startDate) searchParams.set('startDate', params.startDate);
  if (params.endDate) searchParams.set('endDate', params.endDate);
  
  const endpoint = `/dashboard/api/time-series?${searchParams.toString()}`;
  const response = await apiFetch<ApiResponse<TimeSeriesDataPoint[]>>(endpoint);
  
  if (!response.success || !response.data) {
    throw new Error(response.message || '获取时序数据失败');
  }
  
  return response.data;
}

/**
 * 获取Dashboard告警
 */
export async function fetchDashboardAlerts(): Promise<DashboardAlert[]> {
  const response = await apiFetch<ApiResponse<DashboardAlert[]>>('/dashboard/api/alerts');
  
  if (!response.success || !response.data) {
    throw new Error(response.message || '获取告警数据失败');
  }
  
  return response.data;
}

/**
 * 记录手动修正事件
 */
export async function recordManualCorrection(data: {
  practitionerId: string;
  clinicalNoteId: string;
  correctionType: 'drag_adjustment' | 'label_modification' | 'manual_addition';
  correctionCount?: number;
}): Promise<void> {
  const response = await apiFetch<ApiResponse>('/dashboard/api/manual-correction', {
    method: 'POST',
    body: JSON.stringify(data)
  });
  
  if (!response.success) {
    throw new Error(response.message || '记录修正事件失败');
  }
}

/**
 * 导出Dashboard数据
 */
export async function exportDashboardData(params: {
  format: 'csv';
  timeRange?: {
    startDate: string;
    endDate: string;
  };
}): Promise<Blob> {
  const response = await window.fetch(`${API_BASE_URL}/dashboard/api/export`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Dashboard-Secret': DASHBOARD_SECRET,
    },
    body: JSON.stringify(params)
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: '导出失败' }));
    throw new Error(errorData.message || '导出失败');
  }

  return response.blob();
}

// === ETL管理API方法 ===

/**
 * 手动触发ETL作业
 */
export async function runETLJob(jobName: string): Promise<void> {
  const response = await apiFetch<ApiResponse>(`/dashboard/api/etl/run/${jobName}`, {
    method: 'POST'
  });
  
  if (!response.success) {
    throw new Error(response.message || 'ETL作业执行失败');
  }
}

/**
 * 获取ETL作业状态
 */
export async function fetchETLStatus(): Promise<any[]> {
  const response = await apiFetch<ApiResponse<any[]>>('/dashboard/api/etl/status');
  
  if (!response.success || !response.data) {
    throw new Error(response.message || '获取ETL状态失败');
  }
  
  return response.data;
}

/**
 * 获取数据质量报告
 */
export async function fetchDataQuality(): Promise<{
  completeness: number;
  freshness: number;
  accuracy: number;
  issues: string[];
}> {
  const response = await apiFetch<ApiResponse<any>>('/dashboard/api/data-quality');
  
  if (!response.success || !response.data) {
    throw new Error(response.message || '获取数据质量报告失败');
  }
  
  return response.data;
}